import { memo, useContext } from 'react';

import { getCellStyle, getCellClassname } from './utils';
import type { CellRendererProps, SummaryFormatterProps } from './types';
import { SELECT_COLUMN_KEY } from './Columns';
import { css } from '@linaria/core';
import { RowStore } from './store/rowStore';
import { GridStore } from './store/gridStore';
import { isNil } from 'lodash';
import { formatMessage } from './locales';
import { Tooltip } from 'antd';
import { StopOutlined } from '@ant-design/icons'

const selectCellClassName = css`
&.rdg-cell.rdg-cell-frozen {
  background-color: var(--header-background-color);
  padding: 0px;
  color: var(--color);
  &:hover {
    color: var(--color) !important;
  }
}
`

const rowIndexCellClassName = css`
font-size: 12px;
cursor: pointer;
display: flex;
align-items: center;
justify-content: space-between;
padding-right: 5px;
`;

const summaryCellClassname = css`
background: var(--header-background-color);
`;

type SharedCellRendererProps<R, SR> = Pick<CellRendererProps<R, SR>, 'column'>;

interface SummaryCellProps<R, SR> extends SharedCellRendererProps<R, SR> {
  row: SR;
}

function SummaryCell<R, SR>({
  column,
  row
}: SummaryCellProps<R, SR>) {
  const { summaryFormatter: SummaryFormatter = DefaultSummaryFormatter, summaryCellClass } = column;
  const className = getCellClassname(column,
    typeof summaryCellClass === 'function' ? summaryCellClass(row) : summaryCellClass,
    {
      [selectCellClassName]: column.key === SELECT_COLUMN_KEY,
    },
    summaryCellClassname
  );

  return (
    <div
      role="gridcell"
      aria-colindex={column.idx + 1}
      className={className}
      style={getCellStyle(column)}
    >
      {SummaryFormatter && <SummaryFormatter column={column} row={row} />}
    </div>
  );
}

function SelectColumnCell<R, SR>(props: SummaryFormatterProps<SR, R>) {
  const rowStore = useContext(RowStore);
  const gridStore = useContext(GridStore);
  return <span className={rowIndexCellClassName}>
    <Tooltip showArrow={false} title={formatMessage("SummaryCell.SelectColumnCell.unFrozen")}>
    <a style={{ marginRight: 3, marginLeft: 3 }} onClick={() => {
      const rowKey = gridStore.gridRef?.rowKeyGetter(props.row as Record<string, any>);
      const newSet = new Set(rowStore.topRowSet);
      if (!isNil(rowKey)) {
        newSet.delete(rowKey)
        rowStore.changeTopRowSet(newSet);
      }
    }}>
      <StopOutlined />
    </a>
    </Tooltip>
    {props.row[props.column.key as keyof SR]}
  </span>
}

function DefaultSummaryFormatter<R, SR>(props: SummaryFormatterProps<SR, R>) {
  if (props.column.key === SELECT_COLUMN_KEY) {
    return <SelectColumnCell {...props} />
  }
  try {
    return <>{props.row[props.column.key as keyof SR]}</>;
  } catch (e) {
    return null;
  }
}

export default memo(SummaryCell) as <R, SR>(props: SummaryCellProps<R, SR>) => JSX.Element;
